import { setCookie, deleteCookie, getCookie } from "@huub-web/utils";

import type { MaximumMortgageWizardFormValues } from "../types";

/**
 * Default cookie name for storing maximum mortgage wizard data.
 */
const DEFAULT_COOKIE_NAME = "maximum_mortgage";

/**
 * Cookie max age in seconds (7 days).
 * Matches auth cookie expiration.
 */
const COOKIE_MAX_AGE = 7 * 24 * 60 * 60;

export type UseMaximumMortgageCookieOptions = {
  /** Cookie name (environment-specific, e.g., huub_maximum_mortgage_testing) */
  cookieName?: string;
  /** Cookie domain for cross-subdomain sharing (e.g., .huub.nl) */
  cookieDomain?: string;
};

/**
 * Get cookie configuration based on options.
 * - Production: domain=.huub.nl, secure=true
 * - Localhost: no domain (defaults to current host), secure=false
 */
const getCookieConfig = (domain?: string) => {
  const isSecure = !!domain;

  return {
    domain: domain || undefined,
    path: "/",
    secure: isSecure,
    sameSite: "lax" as const,
    maxAge: COOKIE_MAX_AGE,
  };
};

/**
 * Hook for managing maximum mortgage wizard data in a cross-domain cookie.
 * Used to persist wizard form values across auth flows and domain transitions.
 *
 * @param options - Cookie configuration options
 * @param options.cookieName - Environment-specific cookie name (default: maximum_mortgage)
 * @param options.cookieDomain - Domain for cross-subdomain sharing (e.g., .huub.nl)
 */
export const useMaximumMortgageCookie = (
  options: UseMaximumMortgageCookieOptions = {},
) => {
  const { cookieName = DEFAULT_COOKIE_NAME, cookieDomain } = options;

  /**
   * Sets the maximum mortgage cookie with the form values.
   * Cookie is configured for cross-domain sharing between marketing site and app.
   */
  const setMaximumMortgageCookie = (
    data: Partial<MaximumMortgageWizardFormValues>,
  ): void => {
    const config = getCookieConfig(cookieDomain);
    const value = JSON.stringify(data);
    setCookie(cookieName, value, config);
  };

  /**
   * Gets the maximum mortgage form values from the cookie.
   * @returns The form values or null if not found/invalid
   */
  const getMaximumMortgageCookie =
    (): Partial<MaximumMortgageWizardFormValues> | null => {
      const value = getCookie(cookieName);

      if (!value) {
        return null;
      }

      try {
        return JSON.parse(value) as Partial<MaximumMortgageWizardFormValues>;
      } catch {
        return null;
      }
    };

  /**
   * Deletes the maximum mortgage cookie.
   * Must use same domain/path as when setting.
   */
  const deleteMaximumMortgageCookie = (): void => {
    const config = getCookieConfig(cookieDomain);
    deleteCookie(cookieName, {
      domain: config.domain,
      path: config.path,
    });
  };

  return {
    setMaximumMortgageCookie,
    getMaximumMortgageCookie,
    deleteMaximumMortgageCookie,
  };
};
